import { saveUsers } from 'actions';
import { selectUsers, setIsImporting } from 'actions/userPermissionsPage';
import EnvelopeCollapsableSection from 'components/CollapsableSection/EnvelopeCollapsableSection';
import { teamLikeFields, userImportFields } from 'constants/users';
import React from 'react';
import { useDispatch } from 'react-redux';
import { useParams, useRouteMatch } from 'react-router-dom';
import { getCustomerDomain } from 'selectors/auth';
import getUsers, { getImportErrors, getSelectedUsers } from 'selectors/userPermissionsPage';
import { useSelector } from 'store';
import { printStringArray } from 'utils/strings';
import { unknownFields } from 'utils/teams';
import { useHistory } from 'utils/urls';
import ErrorFieldCorrectionCard from './ErrorFieldCorrectionCard';
import SelectedUsersSection from './SelectedUsersSection';
import UsersStats from './UsersStats';

type ComponentProps = {
  footer?: boolean;
  saveInApply?: boolean;
  renderExtraOptions?: boolean;
};

const ImportUserSidebar: React.FC<ComponentProps> = ({
  footer = true,
  saveInApply = false,
  renderExtraOptions = true,
}) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const route = useRouteMatch();

  const { cohortId } = useParams<{ cohortId: string }>();

  const customerDomain = useSelector(getCustomerDomain);
  const selectedUsers = useSelector(getSelectedUsers);
  const users = useSelector(getUsers);
  const importErrors = useSelector(getImportErrors);

  const missingFieldErrors = importErrors.filter((e) => e.error.type === 'missing-field');
  const missingHeaderErrors = importErrors.filter((e) => e.error.type === 'missing-header');

  const handleConfirmSave = (): void => {
    if (cohortId && route.path.includes('import')) {
      history.pushLookup({
        routeName: 'customer-cohort-import',
        customerDomain,
        routeParams: { cohortId },
      });
    } else {
      dispatch(
        saveUsers({
          users,
          redirect: true,
          fromPermissionsPage: true,
        })
      );
    }
  };

  const handleClose = (): void => {
    dispatch(setIsImporting(false));
    dispatch(selectUsers([]));
  };

  const getUnknownFieldInfo = (
    field: string
  ): { value: string; affectedUsers: string[]; hasErrors: boolean }[] => {
    const fieldErrors = importErrors.filter(
      (e) => e.error.type === 'unknown-field' && e.error.field === field
    );

    const info = [
      ...new Set(
        fieldErrors.map((e) => e.error.value).filter((value): value is string => Boolean(value))
      ),
    ].map((value) => ({
      value,
      affectedUsers: [
        ...new Set(fieldErrors.filter((e) => e.error.value === value).map((e) => e.name)),
      ],
      hasErrors: fieldErrors.length > 0,
    }));

    return info;
  };

  return (
    <div className="relative h-full flex flex-col bg-white">
      <div className="flex-1 overflow-auto no-scrollbar">
        <div className="h-full">
          <EnvelopeCollapsableSection headerTitle={`Imported Users (${selectedUsers.length})`}>
            <SelectedUsersSection
              shouldRemoveUsers={!saveInApply}
              canSendInvitation={false}
              renderExtraOptions={renderExtraOptions}
            />
          </EnvelopeCollapsableSection>
          <EnvelopeCollapsableSection headerTitle="Import Details">
            <UsersStats />
          </EnvelopeCollapsableSection>
          {importErrors.length > 0 && (
            <EnvelopeCollapsableSection
              headerTitle="Issue Importing"
              bgColor="bg-litlingo-secondary-80"
            >
              <div className="p-4 flex flex-col gap-4">
                {missingFieldErrors.length > 0 && (
                  <div className="flex flex-col gap-2">
                    <div className="flex flex-col gap-1">
                      <span className="italic">Users that couldn&apos;t be imported</span>
                      <ul className="pl-6">
                        {[...new Set(missingFieldErrors.map((e) => e.name))].map((n) => (
                          <li key={n} className="list-disc list-outside">
                            {n}
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div>
                      Users are missing some of the next properties:{' '}
                      <span className="italic">
                        {printStringArray([
                          ...new Set(missingFieldErrors.map((e) => e.error.field)),
                        ])}
                      </span>
                    </div>
                  </div>
                )}
                {missingHeaderErrors.length > 0 && (
                  <div className="flex flex-col gap-4">
                    {missingHeaderErrors.map(({ error }) => (
                      <p key={error.field}>
                        <span className="font-bold">{userImportFields[error.field].label}</span> was
                        missing in the import file. Make sure the table headers weren&apos;t changed
                        from the original starter file and try importing again.
                      </p>
                    ))}
                  </div>
                )}
                {unknownFields.map(({ field, label }, fieldIdx) => {
                  const info = getUnknownFieldInfo(field);
                  if (info.some((i) => !i.hasErrors)) return null;

                  if (teamLikeFields.includes(field) || field === 'roles') {
                    const fieldCards = info.map((val, idx) => (
                      <>
                        {(fieldIdx !== 0 || idx !== 0) && (
                          <div className="w-full border-b border-litlingo-gray-1" />
                        )}
                        <ErrorFieldCorrectionCard
                          key={`${field}-${val.value}`}
                          field={field}
                          users={val.affectedUsers}
                          value={val.value}
                        />
                      </>
                    ));
                    return fieldCards;
                  }

                  return (
                    <p key={field} className="italic">
                      {info.map((i) => (
                        <>
                          The {label}, <span className="font-bold">{i.value}</span>, was not added
                          for <span className="font-bold">{i.affectedUsers.length} users</span>.
                          Reach out to your customer success representative to add the team into the
                          workspace.
                        </>
                      ))}
                    </p>
                  );
                })}
              </div>
            </EnvelopeCollapsableSection>
          )}
        </div>
      </div>

      {footer && (
        <div
          className="flex flex-col gap-1 bg-white border border-litlingo-gray-1 p-4"
          style={{ filter: 'drop-shadow(3px 1px 8px rgba(0, 0, 0, 0.25))' }}
        >
          <div className="flex flex-row items-center justify-between gap-6 h-8">
            <button
              type="button"
              data-testid="next-envelope-button"
              className="button button--secondary w-27 py-1.5 h-full"
              onClick={handleClose}
            >
              <div className="flex w-full justify-center items-center font-bold">Exit</div>
            </button>
            <button
              type="button"
              id="next-envelope-button"
              data-testid="next-envelope-button"
              className="button  button--primary min-w-27 h-full py-1.5 focus:outline-none"
              onClick={handleConfirmSave}
            >
              <div className="flex w-full justify-center items-center text-base font-bold leading-5">
                Save & Exit
              </div>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
export default ImportUserSidebar;
